import React, { useCallback, useEffect, useState } from "react";
import Layout from "../../Components/Layout";
import ProGrid from "../../Components/ProGrid/v2";
import usePageState from "../../../utils/customHooks/usePageState";
import useColumn from "../../../utils/customHooks/useColumns";
import { STATUS_MAPPINGS, STAUTES } from "../../../utils/constant";
import { useTranslation } from "react-i18next";
import { getApi, postApi } from "../../../utils/services";
import { GET_ALL_PROJECT_BY_EMPLOYEE, GET_APPRENTICE_FORM_STATUS } from "../../../utils/services/apiPath";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { loginSelector } from "../../store/features/auth/authSlice";
import RegistrationPendingModel from "../../Components/RegistrationPendingModel";
import { currencyColumnDef } from "../../Components/ProGrid/components/FormatCell";
import { errorToast } from "../../../utils/toastHelper";
import { createODataQuery } from "../../../utils/services/oData";

export default function ProjectList() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const { user } = useSelector(loginSelector);
  const [pendingItems, setPendingItems] = useState([]);
  const [modalText, setModalText] = useState("");
  const {
    currentPage,
    pageSize,
    data: projectsData,
    rowCount,
    status,
    sortModel,
    searchString,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString
  } = usePageState();

  const columns = useColumn(
    [
      {
        field: "companyName",
        headerName: "Company"
      },
      {
        field: "projectName",
        headerName: "Project"
      },
      {
        field: "journeyman",
        headerName: "Journeyman"
      },
      {
        field: "otjHours",
        headerName: "OTJHours"
      },
      {
        field: "onlineTraining",
        headerName: "OnlineTraining"
      },
      currencyColumnDef({ field: "payout", headerName: "Payout" })
    ],
    true
  );

  const handleProfileUpdate = async () => {
    try {
      const { documentStatus, isFormCompleted } = await getApi(`${GET_APPRENTICE_FORM_STATUS}/${user.employeeId}`);
      const tempPendingItems = Object.entries(STATUS_MAPPINGS)
        .filter(([key]) => documentStatus?.includes(key))
        .map(([, value]) => t(value));
      setPendingItems(tempPendingItems);
      const modalText =
        documentStatus?.length === 2 && documentStatus?.includes("RAP_NOT_ASSIGNED")
          ? t("ApprenticeRegistrationRAPPendingText")
          : t("ApprenticeRegistrationPendingText");
      setModalText(modalText);
      if (!isFormCompleted) setOpen(true);
    } catch (error) {
      errorToast(error);
    }
  };

  const getProjectList = useCallback(async () => {
    setStatus(STAUTES.LOADING);
    const searchableFields = ["projectName", "companyName", "journeyman"];
    const query = createODataQuery(searchableFields, [], "", searchString, sortModel, pageSize, currentPage);
    const queryUrl = `${GET_ALL_PROJECT_BY_EMPLOYEE}/${user?.employeeId}${query}`;
    const response = await getApi(queryUrl);
    setStatus(STAUTES.IDLE);
    if (response?.error) return errorToast(response.error);
    const rowsWithId = (Array.isArray(response) ? response : response?.value || []).map(item => ({
      ...item,
      id: item.projectId,
      onlineTraining: item.onlineTraining? parseFloat(item.onlineTraining).toFixed(2): ""
    }));
    const totalRecords = response?.["@odata.count"] ?? rowsWithId.length;
    setPageState(prevPageInfo => ({...prevPageInfo, data: rowsWithId, rowCount: totalRecords }));
  }, [currentPage, pageSize, sortModel, searchString, setPageState, setStatus, user?.employeeId]);

  useEffect(() => {
    handleProfileUpdate();
    getProjectList();
  }, [getProjectList]);

  return (
    <>
      <Layout>
        <ProGrid
          title={t("Projects")}
          columns={columns}
          loading={status === STAUTES.LOADING}
          rows={projectsData}
          searchMode={!!searchString}
          searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
          hideAddButton={true}
          hideExport={false}
          options={{
            getRowId: row => row.projectId,
            rowCount: rowCount,
            paginationMode: "server",
            paginationModel: { pageSize: pageSize, page: currentPage },
            onPaginationModelChange: setPagination,
            sortingMode: "server",
            onSortModelChange: setSortModel
          }}
        />
      </Layout>

      {user?.isApprentice && (
        <RegistrationPendingModel
          open={open}
          title={t("ApprenticeRegistrationPendingTitle")}
          message={modalText}
          pendingItems={pendingItems}
          onConfirm={() => navigate("/registration")}
          confirmBtnText={t("Ok")}
        />)
      }
    </>
  );
}
